import {
    DownloadOutlined,
    DownOutlined
} from '@ant-design/icons';
import { Breadcrumb, Button, ConfigProvider, DatePicker, PaginationProps, Select, Space, Typography } from 'antd';
import viVN from 'antd/locale/vi_VN';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip as RTooltip, XAxis, YAxis } from 'recharts';
import Layout from '../../../Layout';
import { CampaignStats, exportAnalysisMarketingCampaignsExcel, getListReportCampaign, getReportCampaignCustomerAddress, getReportCampaignCustomerJob, getReportCampaignCustomerTarget, getReportCampaignDemographics, getReportCampaignVehicleDistributionByAge, IReportCampaign, IReportCampaignDemographics, IReportCampaignVehicleDistributionByAge } from '../../../api/reportCustomer/reportCustomerApi';
import { getListBrandName, getListCampaign, ISelectOption } from '../../../api/selectOptions/selectOptionsApi';
import useDebounce from '../../../hook/useDebounce';
import useLoading from '../../../hook/useLoading';
import useToast from '../../../hook/useToast';
import { selectorInfoUser } from '../../../store/slices/infoUserSlice';
import { selectorPermission } from '../../../store/slices/permissionSlice';
import { fommatSelectOptionTitle, formatDateV2, formatPrice, getPermissionClass, getUserPermissions } from '../../../until';
import CampaignTable, { STATUS_LIST } from './CampaignTable';
import StatisticCard from './StatisticCard';
import { ChartCard } from './components/ChartCard';
import { CustomerTypePie } from './components/CustomerTypePie';
import { CustomerTypePieBusiness } from './components/CustomerTypePieBusiness';
import { GenderDonut } from './components/GenderDonut';
import { HorizontalBarChart } from './components/HorizontalBarChart';
import './styles.scss';

export enum TYPEFILTERTOP {
    ADDRESS = 0,
    JOB = 1,
}

const { Title, Text } = Typography;

const convertAgeRangeToValue = (value?: string | null): number | undefined => {
    if (!value) return undefined;

    const normalized = value
        .trim()
        .replace(/\s+/g, "");

    const ageRangeMap: Record<string, number> = {
        "Khôngxácđịnh": 0,
        "<18": 1,
        "18-25": 2,
        "26-35": 3,
        "36-45": 4,
        "46-55": 5,
        ">55": 6,
    };

    return ageRangeMap[normalized];
};

const buildTopWithOther = (
    data: { name: string; value: number }[],
    top: number
) => {
    const sorted = [...data].sort((a, b) => b.value - a.value);
    const main = sorted.slice(0, top - 1);
    const otherSum = sorted.slice(top - 1).reduce((s, i) => s + i.value, 0);

    return otherSum > 0 ? [...main, { name: "Khác", value: otherSum }] : main;
};

const buildTopWithOtherV2 = (
    data: { id: number; name: string; value: number }[] | undefined,
    top: number
): { id: number[]; name: string; value: number }[] => {

    if (!data?.length) return [];

    const sorted = [...data].sort(
        (a, b) => b.value - a.value
    );

    // top = 15 => lấy toàn bộ, không gom "Khác"
    if (top === 15) {
        return sorted.map(item => ({
            ...item,
            id: [item.id],
        }));
    }

    // Các item chính
    const main = sorted
        .slice(0, top - 1)
        .map(item => ({
            ...item,
            id: [item.id],
        }));

    // Các item còn lại gom vào "Khác"
    const others = sorted.slice(top - 1);

    const otherSum = others.reduce(
        (sum, item) => sum + item.value,
        0
    );

    const otherIds = others.map(
        item => item.id
    );

    return otherSum > 0
        ? [
            ...main,
            {
                name: "Khác",
                value: otherSum,
                id: otherIds,
            },
        ]
        : main;
};

interface CustomerFilterSearch {
    campaign?: string
}

export interface TypeTotalReport {
    zns?: CampaignStats
    sms?: CampaignStats
    email?: CampaignStats
}

export interface IDataDefaulFilter {
    campaign?: number[],
    status?: number[],
    brandName?: number[],
    dateCreate?: string,
    dateSend?: string,
}

export const dataDefaulFilter: IDataDefaulFilter = {
};

const AnalysisMarketingCampaigns: React.FC = () => {
    const pushToast = useToast();
    const pushLoading = useLoading()
    const navigate = useNavigate()

    const location = useLocation();
    const { unitTemp } = useSelector(selectorInfoUser)

    const permissionSlice = useSelector(selectorPermission)
    const { add, edit, delete: isdelete, upload, download } = getUserPermissions(permissionSlice, location.pathname)
    const [sLBrandName, setSLBrandName] = useState<ISelectOption[]>([])
    const [sLCampaign, setSLCampaign] = useState<ISelectOption[]>([])
    const [dataReportCampaign, setDataReportCampaign] = useState<IReportCampaign[]>([])
    const [dataReportCampaignDemographics, setDataReportCampaignDemographics] = useState<IReportCampaignDemographics>()
    const [dataReportCampaignVehicleDistributionByAge, setDataReportCampaignVehicleDistributionByAge] = useState<IReportCampaignVehicleDistributionByAge>()
    const [dataReportCampaignCustomerAddress, setDataReportCampaignCustomerAddress] = useState<any[]>()

    const [dataReportCampaignCustomerJob, setDataReportCampaignCustomerJob] = useState<any[]>()

    const [dataReportCampaignCustomerTarget, setDataReportCampaignCustomerTarget] = useState<{ avgAge: number, totalCustomerTarget: number }>()

    const [searchFilter, setSearchFilter] = useState<CustomerFilterSearch>()

    const [dataFilter, setDataFilter] = useState<IDataDefaulFilter>(dataDefaulFilter)
    const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
    const [totalReport, setTotalReport] = useState<TypeTotalReport>()
    const [total, setTotal] = useState<number>()

    const [isExporting, setIsExporting] = useState(false)

    const handleExportExcel = async () => {
        setIsExporting(true)
        await exportAnalysisMarketingCampaignsExcel(
            unitTemp,
            dataFilter?.status, dataFilter?.dateCreate,
            dataFilter?.dateSend, dataFilter?.campaign, dataFilter?.brandName
        )
        setIsExporting(false)
    }

    const [valueTop, setValueTop] = useState<Record<TYPEFILTERTOP, number>>({
        [TYPEFILTERTOP.ADDRESS]: 10,
        [TYPEFILTERTOP.JOB]: 10,
    });


    const dataCampaignVehicleDistributionByAge = useMemo(() => {
        if (!dataReportCampaignVehicleDistributionByAge) return [];
        const sum = (key: keyof IReportCampaignVehicleDistributionByAge) => dataReportCampaignVehicleDistributionByAge[key] || 0;
        return [
            { name: 'Không xác định', value: dataReportCampaignVehicleDistributionByAge.undetermined },
            { name: '< 18', value: dataReportCampaignVehicleDistributionByAge.ageUnder18 },
            { name: '18 - 25', value: dataReportCampaignVehicleDistributionByAge.ageBetween1825 },
            { name: '26 - 35', value: dataReportCampaignVehicleDistributionByAge.ageBetween2635 },
            { name: '36 - 45', value: dataReportCampaignVehicleDistributionByAge.ageBetween3645 },
            { name: '46 - 55', value: dataReportCampaignVehicleDistributionByAge.ageBetween4655 },
            { name: '> 55', value: dataReportCampaignVehicleDistributionByAge.ageOver55 },
        ];
    }, [dataReportCampaignVehicleDistributionByAge]);




    const dataFormatCustomerAddress = useMemo(() => buildTopWithOtherV2(dataReportCampaignCustomerAddress ?? [], valueTop[TYPEFILTERTOP.ADDRESS])
        , [dataReportCampaignCustomerAddress, valueTop[TYPEFILTERTOP.ADDRESS]])


    const dataFormatCustomerJob = useMemo(() => buildTopWithOtherV2(dataReportCampaignCustomerJob ?? [], valueTop[TYPEFILTERTOP.JOB])
        , [dataReportCampaignCustomerJob, valueTop[TYPEFILTERTOP.JOB]])


    const gSLBrandName = useDebounce(async () => {
        pushLoading(true)
        const result = await getListBrandName();
        if (result?.success) {
            setSLBrandName(fommatSelectOptionTitle(result.data))
        } else {
            pushToast(result?.message, "error")
        }
        pushLoading(false)
    }, 500)

    const gSLCampaign = useDebounce(async () => {
        pushLoading(true)
        const result = await getListCampaign(searchFilter?.campaign);
        if (result?.status) {
            setSLCampaign(fommatSelectOptionTitle(result.data))
        } else {
            pushToast(result?.message, "error")
        }
        pushLoading(false)
    }, 500)

    const gListReportCampaign = useDebounce(async (pageCurent: number = 1, pageSize: number = 10) => {
        pushLoading(true)
        const result = await getListReportCampaign(
            pageSize, pageCurent, unitTemp,
            dataFilter?.status, dataFilter?.dateCreate,
            dataFilter?.dateSend, dataFilter?.campaign, dataFilter?.brandName

        )
        if (result.status) {
            setDataReportCampaign(result.data)
            setPagination({ current: pageCurent, total: result.total, pageSize })
            setTotalReport({
                zns: result?.zns,
                sms: result?.sms,
                email: result?.email
            })
            setTotal(result.total)
        }
        pushLoading(false)
    }, 500)

    const gReportCampaignDemographics = useDebounce(async () => {
        pushLoading(true)
        const result = await getReportCampaignDemographics(
            unitTemp,
            dataFilter?.status, dataFilter?.dateCreate,
            dataFilter?.dateSend, dataFilter?.campaign, dataFilter?.brandName

        )
        if (result.status) {
            setDataReportCampaignDemographics(result.data)
            // setTotal(result.total)
        }
        pushLoading(false)
    }, 500)

    const gReportCampaignVehicleDistributionByAge = useDebounce(async () => {
        pushLoading(true)
        const result = await getReportCampaignVehicleDistributionByAge(
            unitTemp,
            dataFilter?.status, dataFilter?.dateCreate,
            dataFilter?.dateSend, dataFilter?.campaign, dataFilter?.brandName

        )
        if (result.status) {
            setDataReportCampaignVehicleDistributionByAge(result.data)
            // setTotal(result.total)
        }
        pushLoading(false)
    }, 500)

    const gReportCampaignCustomerAddress = useDebounce(async () => {
        pushLoading(true)
        const result = await getReportCampaignCustomerAddress(
            unitTemp,
            dataFilter?.status, dataFilter?.dateCreate,
            dataFilter?.dateSend, dataFilter?.campaign, dataFilter?.brandName

        )
        if (result.status) {
            const raw = result.data.map(item => ({
                name: item.province,
                value: item.total,
                id: item.id
            }));
            setDataReportCampaignCustomerAddress(raw)
            // setTotal(result.total)
        }
        pushLoading(false)
    }, 500)



    const gReportCampaignCustomerJob = useDebounce(async () => {
        pushLoading(true)
        const result = await getReportCampaignCustomerJob(
            unitTemp,
            dataFilter?.status, dataFilter?.dateCreate,
            dataFilter?.dateSend, dataFilter?.campaign, dataFilter?.brandName

        )
        if (result.status) {
            const raw = result.data.map(item => ({
                name: item.profession,
                value: item.total,
                id: item.id
            }));
            setDataReportCampaignCustomerJob(raw)
            // setTotal(result.total)
        }
        pushLoading(false)
    }, 500)



    const gReportCampaignCustomerTarget = useDebounce(async () => {
        pushLoading(true)
        const result = await getReportCampaignCustomerTarget(
            unitTemp,
            dataFilter?.status, dataFilter?.dateCreate,
            dataFilter?.dateSend, dataFilter?.campaign, dataFilter?.brandName

        )
        if (result.status) {

            setDataReportCampaignCustomerTarget({ avgAge: result.avgAge ?? 0, totalCustomerTarget: result.totalCustomerTarget ?? 0 })
            // setTotal(result.total)
        }
        pushLoading(false)
    }, 500)

    const onPaginationChange: PaginationProps['onChange'] = (pageNumber, pageSize) => {
        gListReportCampaign(pageNumber, pageSize)
    }


    useEffect(() => {
        gSLCampaign()
    }, [searchFilter?.campaign])

    useEffect(() => {
        gListReportCampaign()
        gReportCampaignDemographics()
        gReportCampaignVehicleDistributionByAge()
        gReportCampaignCustomerAddress()
        gReportCampaignCustomerJob()
        gReportCampaignCustomerTarget()
    }, [dataFilter])

    useEffect(() => {
        gSLBrandName()
        gSLCampaign()

    }, [])




    return (
        <div className='card-issuance-list'>
            <Layout className='custom-sidebar-chart'>

                <Breadcrumb
                    items={[
                        {
                            title: 'THACO AUTO CRM',
                        },
                        {
                            title: <a href="/">Báo cáo phân tích</a>,
                        },
                        {
                            title: 'Chiến dịch CSKH/ Marketing',
                        },
                    ]}
                />

                <div className='title-page'>
                    Chiến dịch CSKH/ Marketing
                </div>

                <div className="custom-page-css flex gap-2">
                    <Select
                        className="custom-page-css border rounded overflow-hidden flex flex-1 custom-scroll-multi-select"
                        placeholder="Tên chiến dịch (chọn nhiều)"
                        mode="multiple"
                        options={sLCampaign}
                        onSearch={(value) => setSearchFilter({ ...searchFilter, campaign: value })}
                        onBlur={() => setSearchFilter({ ...searchFilter, campaign: '' })}
                        onChange={(e) => setDataFilter({ ...dataFilter, campaign: e !== undefined ? e : undefined })}
                        onClear={() => setDataFilter({ ...dataFilter, campaign: undefined })}
                        suffixIcon={<DownOutlined />}
                    />

                    <Select
                        className="custom-page-css border rounded overflow-hidden flex flex-1 custom-scroll-multi-select"
                        mode="multiple"
                        showSearch={false}
                        placeholder="Brandname/ Tên email"
                        options={sLBrandName}
                        onChange={(e) => setDataFilter({ ...dataFilter, brandName: e !== undefined ? e : undefined })}
                        onClear={() => setDataFilter({ ...dataFilter, brandName: undefined })}
                        suffixIcon={<DownOutlined />}
                    />

                    <Select
                        className="custom-page-css border rounded overflow-hidden flex flex-1 custom-scroll-multi-select"
                        placeholder="Trạng thái chiến dịch (chọn nhiều)"
                        showSearch={false}
                        mode="multiple"
                        options={fommatSelectOptionTitle(STATUS_LIST)}
                        onChange={(e) => setDataFilter({ ...dataFilter, status: e !== undefined ? e : undefined })}
                        onClear={() => setDataFilter({ ...dataFilter, status: undefined })}
                        suffixIcon={<DownOutlined />}
                    />
                    <ConfigProvider locale={viVN}>
                        <DatePicker
                            className="custom-input-date custom-page-css flex flex-1 "
                            placeholder="Ngày tạo chiến dịch"
                            format="DD/MM/YYYY"
                            allowClear
                            onChange={(e) => setDataFilter({ ...dataFilter, dateCreate: e ? formatDateV2(e.toDate()) : undefined })}
                        />
                    </ConfigProvider>
                    <ConfigProvider locale={viVN}>
                        <DatePicker
                            className="custom-input-date custom-page-css flex flex-1 "
                            placeholder="Ngày gửi chiến dịch"
                            format="DD/MM/YYYY"
                            allowClear
                            onChange={(e) => setDataFilter({ ...dataFilter, dateSend: e ? formatDateV2(e.toDate()) : undefined })}
                        />
                    </ConfigProvider>

                    <Button
                        className={` ${getPermissionClass(download)}`}
                        icon={<DownloadOutlined />}
                        loading={isExporting}
                        onClick={handleExportExcel}
                    >
                        Xuất báo cáo
                    </Button>
                </div>
                <span className='text-base font-medium pt-4'>Báo cáo tình trạng gửi chiến dịch CSKH/ MKT</span>
                <CampaignTable
                    dataReportCampaign={dataReportCampaign}
                    totalReport={totalReport}
                    pagination={pagination}
                    onPaginationChange={onPaginationChange}
                />
                <div className='custom-page-css flex flex-col gap-1 pt-4'>
                    <span className='custom-page-css text-base font-medium'> Báo cáo nhân khẩu học khách hàng</span>
                    <span className='custom-page-css text-base font-regular text-italic text-344054'>
                        Đang hiển thị dữ liệu tổng hợp từ{" "}
                        <span
                            style={{
                                color: "#1677FF",
                                fontWeight: 500,
                                fontStyle: "normal",
                                padding: "0 4px",
                            }}
                        >
                            {total} chiến dịch
                        </span>{" "}
                        phía trên
                    </span>

                </div>

                <div className='custom-page-css w-full flex  gap-2 pt-4'>

                    <div className="custom-page-css flex flex-1">
                        <StatisticCard
                            colors={['#1890FF', '#005DB2']}
                            title='Tổng số khách hàng mục tiêu'
                            value={dataReportCampaignCustomerTarget?.totalCustomerTarget ?? 0}

                        />
                    </div>
                    <div className="custom-page-css flex flex-1">
                        <StatisticCard
                            colors={['#F59C23', '#F55E09']}
                            title='Độ tuổi trung bình'
                            value={dataReportCampaignCustomerTarget?.avgAge ?? 0}

                        />
                    </div>
                </div>

                <div className="custom-page-css flex items-stretch gap-4 pt-4">
                    <div
                        className="custom-page-css flex flex-1 "
                    >
                        <ChartCard title="Biểu đồ tỉ lệ phân loại khách hàng" >
                            <CustomerTypePieBusiness
                                dataFilter={dataFilter}
                                data={dataReportCampaignDemographics?.businessCheck}
                            />
                        </ChartCard>
                    </div>
                    <div
                        className="custom-page-css flex flex-1 "
                    >
                        <ChartCard title="Biểu đồ tỉ lệ giới tính" >
                            <GenderDonut
                                dataFilter={dataFilter}
                                data={dataReportCampaignDemographics?.gender}
                            />
                        </ChartCard>
                    </div>

                    <div
                        className="custom-page-css flex flex-1 ">
                        <ChartCard title="Biểu đồ  tỉ lệ tình trạng hôn nhân" >
                            <CustomerTypePie
                                dataFilter={dataFilter}
                                data={dataReportCampaignDemographics?.statusMarital}

                            />
                        </ChartCard>
                    </div>
                </div>

                <div className="custom-page-css flex items-stretch gap-4 pt-4">
                    <div
                        className="custom-page-css flex flex-col flex-1 border rounded-2 gap-2 p-6"
                        style={{ border: '1px solid #eaebec' }}
                    >
                        <div className="custom-page-css flex justify-between items-center mb-2">
                            <h3 className="custom-page-css text-344054 text-base font-medium">Biểu đồ phân bố khách hàng theo độ tuổi</h3>
                        </div>
                        <div style={{ width: '100%', height: 420 }}>
                            <ResponsiveContainer>
                                <BarChart
                                    data={dataCampaignVehicleDistributionByAge}
                                    margin={{ top: 8, right: 20, left: 20, bottom: 8 }}

                                    onClick={(params) =>
                                        navigate(
                                            `/analysis-report/customers-card-list`,
                                            {
                                                state: {
                                                    firstPage: '/analysis-report/marketing-campaigns',
                                                    dataFilterCampaign: dataFilter,
                                                    age: convertAgeRangeToValue(params.activeLabel),
                                                    title: `Khách hàng ở độ tuổi ${params.activeLabel}`
                                                }
                                            }
                                        )

                                    }
                                >
                                    <defs>
                                        <linearGradient id="ownershipGradient" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#1890FF" stopOpacity={0.9} />
                                            <stop offset="95%" stopColor="#9DD0FF" stopOpacity={0.7} />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                    <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                                    <YAxis tickFormatter={(value: number) => formatPrice(value)} />
                                    {dataCampaignVehicleDistributionByAge.length > 0 && (
                                        <RTooltip
                                            formatter={(value: number) => [formatPrice(value), 'Tổng số khách hàng']}
                                        />
                                    )}
                                    <Bar dataKey="value" fill="url(#ownershipGradient)" radius={[4, 4, 0, 0]} barSize={60} />
                                </BarChart>
                            </ResponsiveContainer>

                        </div>
                    </div>
                </div>


                <div className="custom-page-css flex items-stretch gap-4 pt-4">
                    <div
                        className="custom-page-css flex flex-1">
                        <HorizontalBarChart
                            link={true}
                            dataFilter={dataFilter}
                            type={TYPEFILTERTOP.ADDRESS}
                            title="Top khách hàng theo khu vực"
                            data={dataFormatCustomerAddress}
                            valueTop={valueTop}
                            setValueTop={setValueTop}
                            color="#8979FF"
                        />
                    </div>

                    <div
                        className="custom-page-css flex flex-1">
                        <HorizontalBarChart
                            link={true}
                            dataFilter={dataFilter}
                            type={TYPEFILTERTOP.JOB}
                            title="Top khách hàng theo nghề nghiệp"
                            data={dataFormatCustomerJob}
                            valueTop={valueTop}
                            setValueTop={setValueTop}
                            color="#1890FF"
                        />
                    </div>
                </div>

            </Layout >
        </div >
    );
}

export default AnalysisMarketingCampaigns;
